import React, { useEffect, useRef, useState } from 'react';
import Sidebar from 'components/Sidebar';
import SearchOverlay from 'components/SearchOverlay';
import ChatArea from 'components/ChatArea';
import ChatInput from 'components/ChatInput';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkConversationAndCreateMessage, getConversation, getConversationMessages } from 'api/conversations';
import { useNavigate, useParams } from 'react-router-dom';
import MobileHeader from 'components/MobileHeader';
import qs from 'query-string';
import { addRecentItem } from 'helpers/recentItems';
import { getAssistant } from 'api/assistants';

export default function Conversations() {

    const { conversationId } = useParams();

    const [sidebar, setSidebar] = useState(false);
    const [search, setSearch] = useState(false);
    const [sticky, setSticky] = useState(false);
    const [lastMessage, setLastMessage] = useState(null);

    const form = useRef();
    const cache = useQueryClient();
    const navigate = useNavigate();

    const { assistant: assistantId } = qs.parse(window.location.search);

    const { data: conversation = {}, isLoading: isConversationLoading } = useQuery({
        queryKey: ['conversations', conversationId],
        queryFn: async () => await getConversation({id: conversationId}),
        enabled: !!conversationId,
    });

    const { data: messages = [], isLoading: isMessagesLoading } = useQuery({
        queryKey: ['messages', conversationId],
        queryFn: async () => await getConversationMessages({id: conversationId}),
        onSuccess: () => {
            cache.invalidateQueries(['conversations', conversationId]);
        },
        enabled: !!conversationId,
    });

    const { data: assistant, isLoading: isAssistantLoading } = useQuery({
        queryKey: ['assistants', assistantId],
        queryFn: async () => await getAssistant({id: conversation?.assistant || assistantId}),
        enabled: !!conversation?.assistant || !!assistantId
    });


    const { mutate: createMessage, status: createMessageStatus } = useMutation({
        mutationKey: ['createMessage'],
        mutationFn: checkConversationAndCreateMessage,
        onSuccess: (data) => {
            if(!conversationId) {
                return navigate(`/conversations/${data.conversation._id}`);
            }
            console.log("Message created successfully:", data);
            cache.invalidateQueries(['messages', conversationId]);
            onMessageSent({message: data.response});
        },
        onError: (error) => {
            console.error("Error creating message:", error);
        }
    });

    useEffect(() => {
        if(!conversation) {
            return;
        }

        addRecentItem({
            type: conversation?.agent ? 'agent' : 'assistant',
            _id: conversation?.agent || conversation?.assistant,
            name: conversation?.name || 'Conversazione',
        });
    }, [conversation]);

    const onMessageSent = () => {
        setLastMessage();
    }

    const onMessageSending = ({message}) => {
        setLastMessage({
            _id: 'temp-id',
            content: message,
            role: 'user',
            you: true,
        });
    }

    const onMessageSubmit = (formData) => {
        const message = formData.get('message');
        onMessageSending({
            message,
        })
        return createMessage({
            id: conversationId,
            body: {
                message,
                assistant: assistantId
            }
        });
    }

    if(lastMessage) {
        messages.push(lastMessage);
    }

    const isLoading = isConversationLoading || isMessagesLoading || isAssistantLoading;

    return (
        <>
            <Sidebar
                visible={sidebar}
                setVisible={setSidebar}
            />

            <SearchOverlay
                visible={search}
                setVisible={setSearch}
            />

            <MobileHeader
                sidebar={sidebar}
                setSidebar={setSidebar}
                setSearch={setSearch}
                search={search}
            />

            <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] overflow-y-auto ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                <div className='z-10 fixed top-0 w-full border-b border-black/6 py-3 px-6 flex justify-between items-center backdrop-blur-sm'>
                    <div className=''>
                        <p className='text-lg leading-none py-2'>{assistant?.name}</p>
                        <p className=''>{conversation?.name}</p>
                    </div>
                </div>

                <div className="relative flex flex-col h-full bg-white">
                    {/* CHAT */}

                    <ChatArea
                        messages={messages}
                        setSticky={setSticky}
                        sticky={sticky}
                        isThinking={isLoading || createMessageStatus === 'pending'}
                    />


                    {/* TEXTAREA */}

                    <ChatInput
                        onMessageSubmit={onMessageSubmit}
                        form={form}
                    />
                </div>
            </div>
        </>
    )
}
